import { Rating, Pagination, Chip, Skeleton } from "@mui/material";
import { ApiRequest } from "GlobalFunctions";
import { useEffect, useState } from "react";
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';
import KinesiologistsFilters from "./Utils/KinesiologistsFilters";
import EmptyElement from "elements/EmptyElement";
import { useNavigate, useLocation } from "react-router-dom";
import { CiStar } from "react-icons/ci";
import { FaWhatsapp } from "react-icons/fa";
import { LiaUserNurseSolid } from "react-icons/lia";

const defaultFilters: any = {
    advanced_wound_care: true,
    online_advice: true,
    locations: [],
    skills: [],
    insurance_types: [],
}

export default function KinesiologistsList(props: any) {

    const navigate = useNavigate();
    const location = useLocation();

    const [list, setList] = useState([]);
    const [filters, setFilters] = useState<any>(defaultFilters);
    const [page, setPage] = useState(() => {
        const p = new URLSearchParams(window.location.search).get('page');
        return p ? parseInt(p) : 1;
    });
    const [totalPages, setTotalPages] = useState(1);
    const [filtersLoaded, setFiltersLoaded] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Blur any focused element so mobile browser doesn't scroll to restore focus
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        const scrollEl = document.getElementById('main_content');
        if (scrollEl) scrollEl.scrollTop = 0;
        // Second reset after 300ms to override any delayed focus-restore scroll
        const t = setTimeout(() => { if (scrollEl) scrollEl.scrollTop = 0; }, 300);

        const params = new URLSearchParams(location.search);
        const newFilters = { ...defaultFilters };
        if (params.has("advanced_wound_care")) newFilters.advanced_wound_care = params.get("advanced_wound_care") === "true";
        if (params.has("online_advice"))       newFilters.online_advice       = params.get("online_advice") === "true";
        if (params.has("locations")) newFilters.locations = params.getAll("locations").map((v) => JSON.parse(v));
        if (params.has("skills"))    newFilters.skills    = params.getAll("skills").map((v) => JSON.parse(v));
        if (params.has("insurance_types")) newFilters.insurance_types = params.getAll("insurance_types").map((v) => JSON.parse(v));
        setFilters(newFilters);
        setFiltersLoaded(true);
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!filtersLoaded) return;

        const params = new URLSearchParams();
        params.set("advanced_wound_care", String(filters.advanced_wound_care));
        params.set("online_advice",       String(filters.online_advice));
        filters.locations.forEach((loc: any)   => params.append("locations", JSON.stringify(loc)));
        filters.skills.forEach((skill: any)    => params.append("skills",    JSON.stringify(skill)));
        filters.insurance_types.forEach((item: any) => params.append("insurance_types", JSON.stringify(item)));
        if (page > 1) params.set("page", String(page));
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });

        setLoading(true);
        setList([]);
        ApiRequest({
            url: '/kinesiologists/list',
            method: 'GET',
            query: { filters, page },
            setResponse: (response: any) => {
                setList(response.list);
                setTotalPages(response.total_pages);
                setLoading(false);
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, page, filtersLoaded]);

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
        const mainContent = document.getElementById('main_content');
        if (mainContent) mainContent.scrollTo({ top: 0, behavior: 'smooth' });
    };

  return (
    <div id='kinesiologists_list_container' className="content">

        <KinesiologistsFilters filters={filters} setFilters={(f: any) => { setPage(1); setFilters(f); }} />

        <div className="nurses_list">
            {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="nurse_card box" style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', padding: '20px' }}>
                        <Skeleton variant="rounded" width={130} height={130} style={{ flexShrink: 0, borderRadius: '12px' }} />
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <Skeleton variant="text" width="50%" height={26} />
                            <Skeleton variant="text" width="30%" height={18} />
                            <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
                                <Skeleton variant="rounded" width={120} height={24} style={{ borderRadius: '16px' }} />
                                <Skeleton variant="rounded" width={80} height={24} style={{ borderRadius: '16px' }} />
                            </div>
                            <Skeleton variant="text" width="100%" height={18} />
                            <Skeleton variant="text" width="80%" height={18} />
                        </div>
                    </div>
                ))
            ) : list.length > 0 ? list.map((item: any, index: number) => (
                <div
                    key={index}
                    className="nurse_card box"
                    onClick={() => navigate(item.alias ? `/${item.alias}` : `/kinesiologos/${item.user_id}`)}
                    style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', padding: '20px' }}
                >
                    <div style={{
                        width: '130px', height: '130px', flexShrink: 0,
                        borderRadius: '12px', overflow: 'hidden',
                        background: '#f0f4f8', display: 'flex',
                        alignItems: 'center', justifyContent: 'center'
                    }}>
                        {item.image
                            ? <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            : <LiaUserNurseSolid size={52} color="#9ca3af" />
                        }
                    </div>

                    <div className="nurse_info" style={{ flex: 1, minWidth: 0 }}>
                        <div className="nurse_header" style={{ marginBottom: '4px' }}>
                            <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', margin: 0 }}>
                                {item.name} {item.lastname}
                            </h2>
                            <Rating
                                value={Number(item.average_rating) || 0}
                                readOnly
                                precision={0.5}
                                size="small"
                                icon={<StarRateRoundedIcon fontSize="inherit" />}
                                emptyIcon={<StarRateRoundedIcon style={{ opacity: 0.4 }} fontSize="inherit" />}
                            />
                        </div>
                        <p className='nurse_subtitle' style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 10px 0' }}>
                            {item.region}
                        </p>
                        <div className="items_row" style={{ gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
                            {item.badge && (
                                <Chip label={item.badge} size="small" icon={<CiStar size={16} />}
                                    sx={{
                                        background: 'linear-gradient(45deg,#FFD700 30%,#DAA520 90%)', color: '#6B4423',
                                        maxWidth: '100%', height: 'auto',
                                        '& .MuiChip-label': { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip', padding: '4px 8px' },
                                    }} />
                            )}
                            {item.whatsapp && (
                                <Chip label="WhatsApp directo" size="small" icon={<FaWhatsapp />} color="success" />
                            )}
                        </div>
                        <p className="nurse_description" style={{
                            fontSize: '14px', color: '#555', lineHeight: '1.6',
                            margin: 0, overflow: 'hidden',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                        }}>
                            {item.about_me}
                        </p>
                    </div>
                </div>
            ))
            : (
                <EmptyElement
                    title="No se encontraron kinesiólogos"
                    subtitle="Intenta con otros filtros de búsqueda."
                />
            )
            }

            {totalPages > 1 && !loading && (
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    sx={{ mt: 2, mb: { xs: 12, md: 2 }, display: 'flex', justifyContent: 'center' }}
                />
            )}
        </div>

    </div>
  );
}
